import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { parsePanelStack, serializePanelStack } from '@/shared/components/side-panel';

import type { AutomationKind } from './api';
import { hoursMinutes } from './model/duration';
import { DEFAULT_LIST_STATE, parseListState, writeListState, type ListState } from './model/filters';
import { reminderValueKey } from './model/reminder';

/** Formats seconds as "1 ч 30 мин" / "45 мин" / "2 ч" in the UI language. */
export function useDurationText() {
  const { t } = useTranslation('tasks');
  return (seconds: number) => {
    const { hours, minutes } = hoursMinutes(seconds);
    if (hours > 0 && minutes > 0) return t('duration.hm', { hours, minutes });
    if (hours > 0) return t('duration.h', { hours });
    return t('duration.m', { minutes });
  };
}

/** The text of a lead time before a deadline: «За 1 час», «Не напоминать». */
export function useReminderText(): (minutes: number) => string {
  const { t } = useTranslation('tasks');
  return useCallback(
    (minutes) => t(`reminder.values.${reminderValueKey(minutes)}`, { minutes }),
    [t],
  );
}

/**
 * How the history and the comments name what made a change that was not a person's: «Робот «Заметка»», «Бизнес-процесс «Отпуск»»,
 * «Триггер «Чек-лист выполнен»» (docs/modules/bizproc.md §3.7) — the kind in the user's language, the name the definition's author gave.
 */
export function useAutomationLabel() {
  const { t } = useTranslation('tasks');
  return useCallback(
    (kind: AutomationKind, name: string) =>
      name ? t(`automation.${kind}`, { name }) : t(`automation.${kind}Plain`),
    [t],
  );
}

export interface ListStateApi {
  state: ListState;
  defaults: ListState;
  /** Merges a change; any change but paging goes back to page 1. */
  update: (patch: Partial<ListState>) => void;
  reset: () => void;
}

/**
 * Toolbar state of a task view: in the page URL (`?preset=…&status=…&q=…`, shareable, survives reloads) or — for views
 * embedded in other modules' pages (group and profile tabs) — in component state.
 */
export function useListState(defaults: Partial<ListState> = {}, mode: 'url' | 'local' = 'url'): ListStateApi {
  const [params, setParams] = useSearchParams();
  const base = useMemo(() => ({ ...DEFAULT_LIST_STATE, ...defaults }), [defaults]);
  const [local, setLocal] = useState<ListState>(base);
  const state = useMemo(
    () => (mode === 'url' ? parseListState(params, base) : local),
    [mode, params, base, local],
  );

  const update = useCallback(
    (patch: Partial<ListState>) => {
      const next: ListState = {
        ...state,
        ...patch,
        page: 'page' in patch && patch.page !== undefined ? patch.page : 1,
      };
      if (mode === 'local') setLocal(next);
      else
        setParams((current) => writeListState(current, next, base), {
          replace: true,
          preventScrollReset: true,
        });
    },
    [state, mode, setParams, base],
  );

  const reset = useCallback(
    () => update({ ...base, preset: state.preset, page: 1 }),
    [update, base, state.preset],
  );
  return { state, defaults: base, update, reset };
}

/** Prefill parameters of the `task:new` panel (MODULE-CONTRACTS §2). */
export const NEW_TASK_PARAMS = ['taskResponsible', 'taskGroup', 'taskParent', 'taskDeadline'] as const;

/** Opens the new-task panel on top of the current panels, optionally prefilled (`task:new?taskResponsible=…`). */
export function useOpenNewTask() {
  const location = useLocation();
  const navigate = useNavigate();
  return useCallback(
    (prefill: { responsibleId?: string; groupId?: string; parentId?: string; deadline?: string } = {}) => {
      const params = new URLSearchParams(location.search);
      for (const key of NEW_TASK_PARAMS) params.delete(key);
      if (prefill.responsibleId) params.set('taskResponsible', prefill.responsibleId);
      if (prefill.groupId) params.set('taskGroup', prefill.groupId);
      if (prefill.parentId) params.set('taskParent', prefill.parentId);
      if (prefill.deadline) params.set('taskDeadline', prefill.deadline);
      const stack = parsePanelStack(location.search).filter((entry) => entry.key !== 'task:new');
      const search = serializePanelStack(params.toString(), [...stack, { type: 'task', id: 'new' }]);
      // Same history marker as useSidePanel().open: closing the panel goes back to this page.
      void navigate(
        { pathname: location.pathname, search },
        { state: { sidePanelPushed: stack.length + 1 }, preventScrollReset: true },
      );
    },
    [location.pathname, location.search, navigate],
  );
}
